import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import type { TeamMember } from '../types';
import OptimizedImage from './OptimizedImage';
import { SocialIcon } from '../pages/public/PublicLayout';

interface TeamMemberModalProps {
    member: TeamMember;
    onClose: () => void;
}

const XIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;

const TeamMemberModal: React.FC<TeamMemberModalProps> = ({ member, onClose }) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = 'auto';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const modalContent = (
        <div 
            className="fixed inset-0 bg-black bg-opacity-70 z-50 grid place-items-center overflow-y-auto p-4 py-10 animate-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="member-name"
        >
            <div 
                className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg md:max-w-3xl lg:max-w-4xl max-h-[90vh] flex flex-col md:flex-row transform transition-transform duration-300 animate-slide-up"
                onClick={e => e.stopPropagation()}
            >
                 <button 
                    onClick={onClose} 
                    className="absolute top-3 right-3 z-20 bg-black/30 text-white rounded-full p-2 hover:bg-black/50 transition-colors md:bg-white/50 md:text-black md:hover:bg-white"
                    aria-label="Close"
                >
                    <XIcon className="w-5 h-5"/>
                </button>

                <div className="w-full md:w-1/3 flex-shrink-0">
                     <OptimizedImage
                        src={member.imageUrl}
                        size={1200}
                        alt={member.name}
                        className="w-full h-64 md:h-full object-cover rounded-t-lg md:rounded-l-lg md:rounded-tr-none"
                        fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=e5e7eb&color=4b5563&size=1200`}
                    />
                </div>
                <div className="flex-1 p-8 overflow-y-auto">
                    <h2 id="member-name" className="text-3xl font-bold font-heading">{member.name}</h2>
                    {member.professionalAffiliation && <p className="text-md text-gray-500">{member.professionalAffiliation}</p>}
                    <p className="text-lg text-gray-800 uppercase tracking-wider font-header mt-1">{member.role}</p>
                    
                    {member.shortDescription && (
                         <p className="text-gray-800 text-lg mt-6 font-body leading-relaxed">{member.shortDescription}</p>
                    )}

                    {member.expertise && member.expertise.length > 0 && (
                        <div className="mt-6">
                            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 font-header mb-3">Key Expertise</h4>
                            <div className="flex flex-wrap gap-2">
                                {member.expertise.map((skill, index) => (
                                    <span key={index} className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {member.socialLinks && member.socialLinks.length > 0 && (
                        <div className="mt-6 pt-6 border-t">
                             <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 font-header mb-3">Connect</h4>
                             <div className="flex items-center space-x-4 flex-wrap gap-y-2">
                                {member.socialLinks.map((link, index) => (
                                    <a 
                                        key={index} 
                                        href={link.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        title={link.platform}
                                        className="text-gray-500 hover:text-brand-dark transition-colors"
                                    >
                                        <SocialIcon platform={link.platform} className="w-6 h-6" />
                                    </a>
                                ))}
                             </div>
                        </div>
                    )}
                </div>
            </div>
            <style>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
                @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                .animate-slide-up { animation: slide-up 0.3s ease-out forwards; }
            `}</style>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    if (!modalRoot) {
        return null;
    }

    return ReactDOM.createPortal(modalContent, modalRoot);
};

export default TeamMemberModal;